import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosRequestHeaders, RawAxiosRequestHeaders } from "axios";
import { Environment } from "../environment/app-settings";
import { RecreateSuccesulResponse, SuccessfulResponse } from "../models/responses/auth-succesfull.response";
import { STORAGEKEYS } from "./keys";
import {apphistory, store} from '../store/index';
import { SessionStorage } from "./cookie-storage";
import { APP_END_LOADING } from "../store/actions/application-actions";
import { AppLinks } from '../environment/app-links'


interface AdaptAxiosRequestConfig extends AxiosRequestConfig {
  headers: AxiosRequestHeaders
}

interface RequestOptions {
  sendFacebookTrackingHeaders?: boolean
}

export interface StartLinkVisitParams {
  isStartLink?: boolean
  source?: string | null
}


export class ApiError extends Error {
  public status?: number
  public data?: any

  constructor(message: string, status?: number, data?: any) {
    super(message)
    this.status = status
    this.data = data
  }
}

export class HttpClient {
  private readonly _instance: AxiosInstance;
  private readonly _storage: SessionStorage;
  private readonly _actionSourceCookieKey = "action_source";
  private readonly _errorStatus = [400, 404, 500];
  private readonly _authErrors = [401, 403];
  private readonly _ignoreErrorMessages = ["timeout of 2000ms exceeded"];


  public constructor(baseURL: string) {
    this._instance = axios.create({
      baseURL,
    });
    
    this._storage = new SessionStorage();
    this._initializeResponseInterceptor();
  }

  private _initializeResponseInterceptor = () => {
    this._instance.interceptors.response.use(
      this._handleResponse,
      this._handleError
    );
  };

  private _handleResponse = (data : AxiosResponse) => data;

  private handleErrorNavigation(navigateTo: string){
    const state = store.getState();
    if(!state.applicationState.isOffline){
      store.dispatch({
        type: APP_END_LOADING
      });

      apphistory.push(navigateTo);
    }
  }

  protected _handleError = (error: any) => {
      if(this._authErrors.filter(p=>p === error.status).length > 0){
        this.handleErrorNavigation(AppLinks.sessionEnd);
      }
      
      return Promise.reject(
        new ApiError(
           error.response?.data?.error_description || error.message || "Unknown error",
           error.response?.status,
           error.response?.data
        )
     );
  };

  private _handleRequest = (config: AdaptAxiosRequestConfig) => {
    const ACCESS_TOKEN = this._storage.get(STORAGEKEYS.ACCESS_TOKEN)
    config.headers["Authorization"] = `bearer ${ACCESS_TOKEN}`
    this._appendActionSourceHeader(config.headers);
  
    return config;
  };

  private _appendActionSourceHeader = (headers: AxiosRequestHeaders | RawAxiosRequestHeaders) => {
    const actionSource = this._storage.getCookie(this._actionSourceCookieKey);
    if (actionSource) {
      headers["x-action-source"] = actionSource;
    }
  }

  private _appendFacebookTrackingHeaders = (headers: AxiosRequestHeaders | RawAxiosRequestHeaders) => {
    const fbc = this._storage.getCookie("_fbc");
    const fbp = this._storage.getCookie("_fbp");

    if (fbc) {
      headers["x-fbc"] = fbc;
    }

    if (fbp) {
      headers["x-fbp"] = fbp;
    }
  }

  public get<T>(url: string, isAuth: boolean = false, timeout: number = 10000): Promise<AxiosResponse<T>> {
    if (isAuth) {
      this._instance.interceptors.request.use(this._handleRequest);
    }
    return this._instance.get(url, {
      headers: {
        "content-type": "application/json",
        "x-action-source": this._storage.getCookie(this._actionSourceCookieKey) || "website"
      },
      timeout: timeout,
      withCredentials : true,
    });
  }

  public post<T, Body>(
    url: string,
    body: Body,
    isAuth: boolean = false,
    signal?: AbortSignal,
    options?: RequestOptions
  ): Promise<AxiosResponse<T>> {
    if (isAuth) {
      this._instance.interceptors.request.use(this._handleRequest);
    }

    const headers: RawAxiosRequestHeaders = {
      "content-type": "application/json",
      "x-action-source": this._storage.getCookie(this._actionSourceCookieKey) || "website"
    };

    if (options?.sendFacebookTrackingHeaders) {
      this._appendFacebookTrackingHeaders(headers);
    }

    return this._instance.post(url, body, {
      headers,
      withCredentials : true,
      signal: signal
    });
  }

  public download (url: string, isAuth: boolean = false) : Promise<AxiosResponse<Blob>> {
    if(isAuth){
      this._instance.interceptors.request.use(this._handleRequest);
    }

    return this._instance.get(url, {
      headers: {
        "content-type": "application/json",
        "x-action-source": this._storage.getCookie(this._actionSourceCookieKey) || "website"
      },
      withCredentials : true,
      responseType: 'blob'
    });
  }

  public authRequest(orderId : string, startLinkVisit?: StartLinkVisitParams): Promise<AxiosResponse<SuccessfulResponse>> {
    const formData = new URLSearchParams();

    formData.append("client_id", Environment.clientId);
    formData.append("client_secret", Environment.clientSecret);
    formData.append("scope", Environment.scope);
    formData.append("grant_type", Environment.grantType);
    formData.append("orderId", orderId);
    this.appendStartLinkVisitParams(formData, startLinkVisit)

    return this._instance.post("/connect/token", formData.toString(), {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
    });
  }

  public authRequestByUuid(uuid: string) : Promise<AxiosResponse<RecreateSuccesulResponse>>{
    const formData = new URLSearchParams();

    formData.append("client_id", Environment.clientId);
    formData.append("client_secret", Environment.clientSecret);
    formData.append("scope", Environment.scope);
    formData.append("grant_type", Environment.grantType);
    formData.append("bm_uuid", uuid);

    return this._instance.post("/connect/token/bm", formData.toString(), {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
    });
  }

  private appendStartLinkVisitParams(formData: URLSearchParams, startLinkVisit?: StartLinkVisitParams) {
    if (!startLinkVisit?.isStartLink) {
      return
    }

    formData.append("isStartLink", "true")
    const source = startLinkVisit.source?.trim()
    if (source) {
      formData.append("source", source)
    }
  }
}